import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useStore } from "store/zustand";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  getAuth,
  signInWithPopup,
} from "firebase/auth";
import {
  CreateAccountApi,
  VerifyLoginEmailApi,
  reportFailedLogin,
  reportLoginSuccess,
} from "apis/authServices";
import { useToastStore } from "store/toastStore";
import axiosClient from "apis/axios-client";
import { getUserDetails } from "apis/homeServices";
import { useUserStore } from "store/userStore";
import { isAdminRole } from "utils/roles";
import OfferModal, { Offer } from "components/OfferModal";
import {
  getFirebaseAuthErrorMessage,
  isPopupCancel,
  isLoginFailureCode,
} from "utils/firebaseErrors";
import LoginBg from "../../assets/images/login.png";
import StandbyIcon from "../../assets/images/standby.png";

// VerifyLoginEmailApi (backend's VerifyLoginEmail) checks the 5-failed-attempts
// lock before anything else, so it rejects even a correct password with a
// 423 while the account is still locked — this has no statusId, so it must
// be checked before the statusId switch below, not folded into it.
const getLockoutMessage = (verifyResponse: any): string | null => {
  if (
    verifyResponse?.statusCode !== 423 &&
    verifyResponse?.statusName !== "TemporarilyLocked"
  ) {
    return null;
  }
  if (verifyResponse?.lockedUntil) {
    const msRemaining =
      new Date(verifyResponse.lockedUntil).getTime() - Date.now();
    if (msRemaining > 0) {
      const minutes = Math.ceil(msRemaining / 60000);
      return `Account temporarily locked due to too many failed login attempts. Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`;
    }
  }
  return (
    verifyResponse?.message ||
    "Account temporarily locked due to too many failed login attempts. Please try again later."
  );
};

const Login: React.FC = () => {
  const { setIsAuthenticated } = useStore();
  const { showLoader, hideLoader } = useGlobalLoader();
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  // One-off notice shown after a forced logout (e.g. account blocked/deleted),
  // stashed in sessionStorage by the axios interceptor before it redirected.
  const [authNotice, setAuthNotice] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passwordLengthError, setPasswordLengthError] = useState<string>("");
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    // Try to load from localStorage on mount
    return localStorage.getItem("rememberMe") === "true";
  });
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Surface any one-off auth notice (account blocked/deleted) left by the axios
  // interceptor on a forced logout, then clear it so it shows only once.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("authNotice");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.message) setAuthNotice(parsed.message);
        sessionStorage.removeItem("authNotice");
      }
    } catch {
      // ignore malformed/unavailable storage
    }
  }, []);
  const googleProvider = new GoogleAuthProvider();
  const appleProvider = new OAuthProvider("apple.com");
  appleProvider.addScope("email");
  appleProvider.addScope("name");
  appleProvider.setCustomParameters({ locale: "fr" });

  const { hideToast } = useToastStore();
  const [banners, setBanners] = useState<Offer[]>([]);
  const [isBannerLoading, setIsBannerLoading] = useState<boolean>(false);
  const [bannerError, setBannerError] = useState<string>("");
  const [currentBannerIndex, setCurrentBannerIndex] = useState<number>(0);
  const [showOfferModal, setShowOfferModal] = useState<boolean>(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  // On mount, if rememberMe, load username and password
  useEffect(() => {
    const shouldRemember = localStorage.getItem("rememberMe") === "true";
    if (shouldRemember) {
      const savedUsername = localStorage.getItem("rememberedUsername") || "";
      const savedPassword = localStorage.getItem("rememberedPassword") || "";
      setUsername(savedUsername);
      setPassword(savedPassword);
    }
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      setIsBannerLoading(true);
      setBannerError("");
      try {
        const data = await axiosClient.get("/Public/GetActiveSignupBanners");
        if (Array.isArray(data)) {
          setBanners(data as Offer[]);
          setCurrentBannerIndex(0);
        } else {
          setBannerError("Unable to load offers right now.");
        }
      } catch (error) {
        console.error("Failed to fetch signup banners:", error);
        setBannerError("Unable to load offers right now.");
      } finally {
        setIsBannerLoading(false);
      }
    };

    fetchBanners();
  }, []);

  useEffect(() => {
    if (currentBannerIndex >= banners.length) {
      setCurrentBannerIndex(0);
    }
  }, [banners.length, currentBannerIndex]);

  const showPreviousBanner = () => {
    setCurrentBannerIndex((prev) =>
      prev === 0 ? banners.length - 1 : prev - 1,
    );
  };

  const showNextBanner = () => {
    setCurrentBannerIndex((prev) =>
      prev === banners.length - 1 ? 0 : prev + 1,
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    showLoader();

    try {
      if (!username) {
        setError(`Email is required`);
        if (usernameRef.current) {
          usernameRef.current.focus();
        }
        return;
      }
      if (!username.includes("@")) {
        setError("Please enter a valid email address.");
        if (usernameRef.current) {
          usernameRef.current.focus();
        }
        return;
      }
      if (!password) {
        setError(`Password is required`);
        if (passwordRef.current) {
          passwordRef.current.focus(); // <-- Focus password input
        }
        return;
      }

      const verifyResponse = await VerifyLoginEmailApi(username);

      const lockoutMessage = getLockoutMessage(verifyResponse);
      if (lockoutMessage) {
        setError(lockoutMessage);
        return;
      }

      const statusId = verifyResponse?.statusId ?? null;

      switch (statusId) {
        case 1: {
          const auth = getAuth(); // ✅ Lexical declaration in block scope
          // Inactive → Redirect to Create Account
          useToastStore
            .getState()
            .showToast("Inactive account. Reactivate your account...", "info");
          await signInWithEmailAndPassword(auth, username, password);
          setTimeout(() => navigate("/createAccount"), 3000);

          return;
        }

        case 2: // Active → Proceed to login
          break;

        case 3: // Blocked → Show error
        case 4: // Deleted → Show error
          setError(verifyResponse.message);
          return;

        case null: // Email does not exist
          useToastStore
            .getState()
            .showToast(
              "This email does not belong to an account. Please use an existing email or sign up.",
              "error",
            );
          return;

        default:
          setError("Unexpected response from the server. Please try again.");
          return;
      }

      //Proceed with login if status is Active (case 2)
      const auth = getAuth();
      const userCredential = await signInWithEmailAndPassword(
        auth,
        username,
        password,
      );
      const user = userCredential.user;
      await user.getIdToken();
      reportLoginSuccess(username);

      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("rememberedUsername", username);
        localStorage.setItem("rememberedPassword", password);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("rememberedUsername");
        localStorage.removeItem("rememberedPassword");
      }

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);
        sessionStorage.removeItem("giveawayWinnerBannerDismissed");

        setIsAuthenticated(true);

        // Navigate based on role
        if (isAdminRole(userDetails.role)) {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setIsAuthenticated(true);
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      // Backend only sees a failed attempt if we tell it — password checking
      // happens against Firebase directly and never touches our API.
      if (isLoginFailureCode(error) && username) {
        reportFailedLogin(username);
      }
      setError(
        getFirebaseAuthErrorMessage(
          error,
          "*Login failed. Please check your credentials.",
        ),
      );
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  // iOS/Safari leaves the last character revealed when -webkit-text-security is
  // toggled back on (eye off). Forcing a reflow repaints every glyph as a dot.
  useEffect(() => {
    const el = passwordRef.current;
    if (!el) return;
    const prev = el.style.display;
    el.style.display = "none";
    void el.offsetHeight; // trigger reflow
    el.style.display = prev;
  }, [showPassword]);

  const signInWithGoogle = async () => {
    setIsLoading(true);
    showLoader();
    setError(""); // Clear any previous errors
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Verify account status
      const email = user.email;
      if (!email) {
        setError("Error: Unable to retrieve email from Google.");
        useToastStore
          .getState()
          .showToast("Error: Unable to retrieve email from Google.", "error");
        return;
      }

      reportLoginSuccess(email);

      const verifyResponse = await VerifyLoginEmailApi(email);

      const googleLockoutMessage = getLockoutMessage(verifyResponse);
      if (googleLockoutMessage) {
        setError(googleLockoutMessage);
        useToastStore.getState().showToast(googleLockoutMessage, "error");
        return;
      }

      const statusId = verifyResponse?.statusId ?? null;

      switch (statusId) {
        case 1: // Inactive
          useToastStore
            .getState()
            .showToast("Inactive account. Reactivate your account...", "info");
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
        case 3: // Blocked
        case 4: // Deleted
          setError(verifyResponse.message || "Account is blocked or deleted.");
          useToastStore
            .getState()
            .showToast(
              verifyResponse.message || "Account is blocked or deleted.",
              "error",
            );
          return;
        case null: // Email does not exist
          useToastStore
            .getState()
            .showToast("Inactive account. Reactivate your account...", "info");
          await CreateAccountApi();
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
      }

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);
        sessionStorage.removeItem("giveawayWinnerBannerDismissed");

        setIsAuthenticated(true);
        useToastStore
          .getState()
          .showToast("Successfully signed in with Google!", "success");

        // Navigate based on role
        if (isAdminRole(userDetails.role)) {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setIsAuthenticated(true);
        useToastStore
          .getState()
          .showToast("Error fetching user details. Redirecting...", "info");
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      console.error("Google sign-in error:", error);

      // User intentionally dismissed the popup — not a real error.
      if (isPopupCancel(error)) return;

      const errorMessage = getFirebaseAuthErrorMessage(
        error,
        "Google sign-in failed. Please try again.",
      );
      setError(errorMessage);
      useToastStore.getState().showToast(errorMessage, "error");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  const signInWithApple = async () => {
    setIsLoading(true);
    showLoader();
    try {
      const auth = getAuth();
      const result = await signInWithPopup(auth, appleProvider);
      const user = result.user;

      // Verify account status
      const email = user.email;
      if (!email) {
        setError("Error: Unable to retrieve email from Apple.");
        return;
      }

      reportLoginSuccess(email);

      const verifyResponse = await VerifyLoginEmailApi(email);

      const appleLockoutMessage = getLockoutMessage(verifyResponse);
      if (appleLockoutMessage) {
        setError(appleLockoutMessage);
        useToastStore.getState().showToast(appleLockoutMessage, "error");
        return;
      }

      const statusId = verifyResponse?.statusId ?? null;

      switch (statusId) {
        case 1: // Inactive
          useToastStore
            .getState()
            .showToast("Inactive account. Reactivate your account...", "info");
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
        case 3: // Blocked
        case 4: // Deleted
          setError(verifyResponse.message);
          return;
        case null: // Email does not exist
          useToastStore
            .getState()
            .showToast("Inactive account. Reactivate your account...", "info");
          await CreateAccountApi();
          setTimeout(() => navigate("/createAccount"), 3000);
          return;
      }

      // Fetch user details to get role and navigate accordingly
      try {
        const userDetails: any = await getUserDetails();

        // Store user details in userStore
        useUserStore.getState().setUsername(userDetails.userName);
        useUserStore.getState().setEmail(userDetails.email);
        useUserStore.getState().setUserRole(userDetails.role);
        useUserStore.getState().setReferralCode(userDetails.referralCode);
        sessionStorage.removeItem("giveawayWinnerBannerDismissed");

        setIsAuthenticated(true);

        // Navigate based on role
        if (isAdminRole(userDetails.role)) {
          navigate("/admin/dashboard");
        } else {
          navigate("/search");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setIsAuthenticated(true);
        // Fallback to /search if user details fetch fails
        navigate("/search");
      }
    } catch (error: any) {
      console.error("Apple sign-in error:", error);

      // User intentionally dismissed the popup — not a real error.
      if (isPopupCancel(error)) return;

      const errorMessage = getFirebaseAuthErrorMessage(
        error,
        "Apple sign-in failed. Please try again.",
      );
      setError(errorMessage);
      useToastStore.getState().showToast(errorMessage, "error");
    } finally {
      hideLoader();
      setIsLoading(false);
    }
  };

  // Memoize password toggle SVG to prevent re-renders
  const passwordToggleIcon = useMemo(
    () => (
      <svg
        className="shrink-0 size-3.5"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          className={showPassword ? "hidden" : ""}
          d="M9.88 9.88a3 3 0 1 0 4.24 4.24"
        />
        <path
          className={showPassword ? "hidden" : ""}
          d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"
        />
        <path
          className={showPassword ? "hidden" : ""}
          d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"
        />
        <line
          className={showPassword ? "hidden" : ""}
          x1="2"
          x2="22"
          y1="2"
          y2="22"
        />
        <path
          className={showPassword ? "" : "hidden"}
          d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"
        />
        <circle
          className={showPassword ? "" : "hidden"}
          cx="12"
          cy="12"
          r="3"
        />
      </svg>
    ),
    [showPassword],
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 50 }}>
      {/* Full-page Background Image */}
      <img
        src={LoginBg}
        alt="Login background"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
        }}
      />

      {/* Content Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          width: "100%",
          height: "100%",
          overflowY: isMobile ? "auto" : "hidden",
        }}
      >
        {/* Left - Logo & Tagline */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile ? "1.5rem" : "2.5rem",
          }}
        >
          <div className="flex items-center gap-2">
            <img src={StandbyIcon} alt="Standby" className="w-8 h-8" />
            <span className="text-white text-xl font-bold tracking-wide">
              STANDBY
            </span>
          </div>
          {!isMobile && (
            <div className="mb-10">
              <h1 className="text-white text-4xl font-bold leading-tight">
                Connecting <span className="text-light-yellow">Air Crews,</span>
                <br />
                <span className="italic">Seamlessly.</span>
              </h1>
            </div>
          )}
        </div>

        {/* Right - Form Card */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "1.5rem 1rem" : "2rem 3rem",
            minWidth: isMobile ? 0 : "580px",
            maxWidth: isMobile ? "100%" : "680px",
            width: isMobile ? "100%" : undefined,
            height: "100%",
            overflowY: isMobile ? "auto" : "hidden",
          }}
        >
          <div
            className="w-full"
            style={{
              maxWidth: isMobile ? "28rem" : "36rem",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div
              className={
                isMobile
                  ? "bg-white rounded-2xl shadow-lg px-6 py-8"
                  : "bg-white rounded-2xl shadow-lg px-10 py-10 sm:px-12 sm:py-11"
              }
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-grease-100 text-center">
                Sign In
              </h2>
              <p className="text-sm mt-1.5 font-normal text-grease-50 text-center">
                Welcome Back, You've been missed.
              </p>

              {authNotice && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 text-center">
                  {authNotice}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <div
                  onClick={signInWithGoogle}
                  className="p-3 border border-gray-200 rounded-lg flex gap-2.5 items-center justify-center flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img
                    className="w-5 h-5 flex-shrink-0"
                    src="/images/_Google.svg"
                    alt="Google logo"
                  />
                  <p className="text-grease-100 text-sm font-medium whitespace-nowrap">
                    Sign In with Google
                  </p>
                </div>
                <div
                  onClick={signInWithApple}
                  className="p-3 border border-gray-200 rounded-lg flex gap-2.5 items-center justify-center flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <img
                    className="w-5 h-5 flex-shrink-0"
                    src="/images/apple.svg"
                    alt="Apple logo"
                  />
                  <p className="text-grease-100 text-sm font-medium whitespace-nowrap">
                    Sign In with Apple
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4 my-4 sm:my-5">
                <div className="w-full h-px bg-gray-200"></div>
                <p className="text-grease-50 text-sm">Or</p>
                <div className="w-full h-px bg-gray-200"></div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-medium text-grease-100 mb-1.5"
                  >
                    Email Address
                  </label>
                  <input
                    type="text"
                    name="username"
                    id="username"
                    ref={usernameRef}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="block w-full p-3 sm:p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400 focus:outline-none border border-gray-200 focus:border-light-yellow focus:ring-1 focus:ring-light-yellow"
                    placeholder="Email Address"
                  />
                </div>
                <div>
                  <label
                    htmlFor="hs-toggle-password"
                    className="block text-sm font-medium text-grease-100 mb-1.5"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="hs-toggle-password"
                      type="text"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      value={password}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length > 20) {
                          setPasswordLengthError(
                            "Password must be 20 characters or less",
                          );
                          setPassword(value.substring(0, 20));
                        } else {
                          setPasswordLengthError("");
                          setPassword(value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          password.length >= 20 &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight" &&
                          e.key !== "Tab" &&
                          e.key !== "Home" &&
                          e.key !== "End" &&
                          !e.ctrlKey &&
                          !e.metaKey &&
                          !e.altKey
                        ) {
                          e.preventDefault();
                          setPasswordLengthError(
                            "Password must be 20 characters or less",
                          );
                        }
                      }}
                      ref={passwordRef}
                      minLength={6}
                      maxLength={20}
                      className={`pw-field ${showPassword ? "" : "pw-mask"} block w-full p-3 sm:p-3.5 rounded-lg bg-white text-sm text-grease-100 placeholder-gray-400 focus:outline-none border border-gray-200 focus:border-light-yellow focus:ring-1 focus:ring-light-yellow`}
                      placeholder="Enter password"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-grease-100 rounded-e-md focus:outline-none"
                    >
                      {passwordToggleIcon}
                    </button>
                  </div>
                </div>
                {passwordLengthError && (
                  <p className="text-red-500 mt-1 text-sm">
                    {passwordLengthError}
                  </p>
                )}
                {error && <p className="text-red-500 mt-1 text-sm">{error}</p>}
                <div className="flex items-center justify-between pt-0.5">
                  <div className="flex items-center">
                    <input
                      id="checked-checkbox"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 text-light-yellow bg-gray-100 border-light-yellow rounded focus:outline-none"
                    />
                    <label
                      htmlFor="checked-checkbox"
                      className="ms-2 text-xs font-medium text-grease-100"
                    >
                      Remember me
                    </label>
                  </div>
                  <button
                    type="button"
                    className="text-xs cursor-pointer bg-transparent border-none p-0 text-grease-100 hover:underline"
                    onClick={() => {
                      navigate("/forgetPassword");
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full p-3.5 rounded-lg text-[#192227] text-center font-semibold bg-light-yellow text-sm mt-4 sm:mt-5 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                >
                  {isLoading ? "Logging In..." : "Log In"}
                </button>
              </form>
              <div className="flex items-center justify-center text-sm gap-1 mt-4">
                <p className="text-grease-200">Don't have account?</p>
                <button
                  type="button"
                  onClick={() => {
                    hideToast();
                    navigate("/signup");
                  }}
                  className="text-light-yellow font-semibold cursor-pointer bg-transparent border-none p-0 hover:underline"
                >
                  Sign Up
                </button>
              </div>
              {(isBannerLoading || bannerError || banners.length > 0) && (
                <div className="mt-4">
                  {isBannerLoading ? (
                    <p className="text-xs text-grease-200 text-center">
                      Loading offers...
                    </p>
                  ) : bannerError ? (
                    <p className="text-xs text-red-500 text-center">
                      {bannerError}
                    </p>
                  ) : banners.length > 0 ? (
                    <>
                      {/* New Banner Design */}
                      <div className="relative bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex items-center gap-3.5">
                        <div className="bg-red-50 p-2 rounded-lg shrink-0">
                          <img
                            src="/images/gift.png"
                            width="22"
                            height="22"
                            alt="Gift"
                          />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs sm:text-sm text-grease-100 font-medium">
                            New offers are waiting for you!{" "}
                            <button
                              onClick={() => setShowOfferModal(true)}
                              className="text-light-yellow font-semibold hover:underline focus:outline-none"
                            >
                              click here
                            </button>{" "}
                            to explore.
                          </p>
                        </div>
                        <button
                          onClick={() => setBanners([])}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <i className="st-close text-xs"></i>
                        </button>
                      </div>

                      {/* Offer Modal */}
                      <OfferModal
                        isOpen={showOfferModal}
                        onClose={() => setShowOfferModal(false)}
                        offer={banners[currentBannerIndex]}
                        currentIndex={currentBannerIndex}
                        totalOffers={banners.length}
                        onNext={showNextBanner}
                        onPrev={showPreviousBanner}
                      />
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
